import { Injectable, signal, inject, DestroyRef, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
	BehaviorSubject,
	Observable,
	auditTime,
	distinctUntilChanged,
	interval,
	map,
	tap,
	withLatestFrom,
} from 'rxjs';

import {
	IBestResults,
	IRaceClass,
	IRaceRgps,
	IRaceSession,
	ISectorData,
	ISessionEntry,
	ISessionInfo,
	ITimingAnalysis,
} from './IRaceTiming';

import { WebSocketService } from '../shared/websocket.service';
import { ThemeService } from '../shared/theme.service';
import { StreamData } from '../shared/stream.token';
import { RACE_DATA_FROM_EXTERNAL_SOURCE } from '../lxp/lxp.tokens';

export type PositionChangeState = 'up' | 'down' | 'neutral';

export function sortEntriesByPosition(entries: ISessionEntry[]): ISessionEntry[] {
	return [...entries].sort((a, b) => {
		const standingA = a.activeStanding;
		const standingB = b.activeStanding;
		// 1. Primary sort: Position number (lower = better)
		if (standingA.position !== standingB.position) {
			return standingA.position - standingB.position;
		}
		return 0;
	});
}

@Injectable({
	providedIn: 'root',
})
export class RaceSessionService {
	private static readonly POSITION_CHANGE_DISPLAY_MS = 10000;
	public readonly isRaceDataFromExternalSource = inject(RACE_DATA_FROM_EXTERNAL_SOURCE, { optional: true }) ?? false;

	private _raceHappening = signal(false);
	public raceHappening = this._raceHappening.asReadonly();
	private readonly selectedClassSubject$ = new BehaviorSubject<string[]>([]);
	public selectedClasses$ = this.selectedClassSubject$.asObservable();
	public isSessionArchivedSubject = new BehaviorSubject<boolean>(false);
	public isSessionArchived$ = this.isSessionArchivedSubject.asObservable();

	private destroyRef = inject(DestroyRef);
	public webSocketService = inject(WebSocketService);
	public themeService = inject(ThemeService);

	public readonly gapByClass = true;

	public raceSession$: Observable<IRaceSession> = this.webSocketService.session$.pipe(
		map((data) => {
			this.isSessionArchivedSubject.next(data.isSessionArchived);
			return data.session;
		}),
	);

	public stints = signal({});
	public timingAnalysis$: Observable<ITimingAnalysis> = this.webSocketService.timingAnalysis$.pipe(
		auditTime(2000),
		tap((data) => this.stints.set(data.stints)),
	);

	public isRaceHappening$: Observable<boolean> = this.raceSession$.pipe(
		map((data: IRaceSession) => {
			return data?.entries?.length > 0;
		}),
		distinctUntilChanged(),
		tap((isRaceInProgress) => {
			this._raceHappening.set(isRaceInProgress);
		}),
	);

	public raceClasses$: Observable<IRaceClass[]> = this.raceSession$.pipe(
		map((data: IRaceSession) => {
			const classes = Object.values(data?.classes || {});
			return classes;
		}),
		distinctUntilChanged(
			(prev, curr) =>
				prev.length === curr.length && prev.every((val, idx) => val.name === curr[idx].name),
		),
	);

	public raceInfo$: Observable<ISessionInfo> = this.raceSession$.pipe(
		map((data: IRaceSession) => data?.info),
	);

	public raceStatus$: Observable<any> = this.raceSession$.pipe(
		map((data: IRaceSession) => data?.status),
	);

	public timeStatus$: Observable<any> = interval(1000).pipe(
		withLatestFrom(this.raceStatus$),
		map(([_, raceStatus]) => {
			if (!raceStatus) {
				return {
					elapsedTime: '00:00:00',
					remainingTime: '00:00:00',
				};
			}
			const start = raceStatus.sessionStartTime;
			const total = raceStatus.finalTime;
			const isFinished = raceStatus.isFinished;

			if (isFinished || !start || !total) {
				return {
					elapsedTime: '00:00:00',
					remainingTime: '00:00:00',
				};
			}

			const now = Date.now();
			const elapsedMs = Math.floor(now - start);
			const remainingMs = Math.max(total * 1000 - elapsedMs, 0);

			return {
				elapsedTime: elapsedMs,
				remainingTime: remainingMs,
			};
		}),
	);

	public raceControlCurrentMessages = signal([]);
	public raceControlAllMessages = signal(new Map());
	public raceControl$ = this.webSocketService.raceControl$.pipe(
		map((raceControlData) => {
			const currentMessages = raceControlData?.currentMessages ?? {};
			const allMessages = raceControlData?.messages;

			Object.entries(currentMessages).forEach(([key, message]) => {
				const messageId = parseInt(key);
				if (message == null) {
					this.raceControlCurrentMessages.update((q) => q.filter((m) => m.id === messageId));
					return;
				}

				this.raceControlCurrentMessages.update((q) => [...q, message]);
			});

			if (allMessages) {
				Object.entries(allMessages).forEach(([key, message]) => {
					this.raceControlAllMessages().set(key, message);
				});
			}
		}),
	);

	public gpsPosition = signal([]);
	public gpsPosition$ = this.webSocketService.rgps$.pipe(
		map((rgps: IRaceRgps) => {
			if (!rgps) {
				return [];
			}
			const gpsData = Object.keys(rgps).map((car) => {
				const gpsData = rgps[car];
				return {
					[car]: {
						lat: gpsData?.lat / 1000000,
						lon: gpsData?.lon / 1000000,
						status: gpsData?.status,
					},
				};
			});
			this.gpsPosition.set([...gpsData]);
			return gpsData;
		}),
	);

	public mainStream$: Observable<StreamData[] | null> = this.webSocketService.mainStream$.pipe(
		distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
	);

	public raceTeamsPosition = signal<ISessionEntry[]>([]);
	private previousPositionByNumber = signal<Record<string, number>>({});
	private currentPositionByNumber = signal<Record<string, number>>({});
	private transientPositionChangeStateByNumber = signal<Record<string, PositionChangeState>>({});
	private readonly positionChangeTimeoutsByNumber = new Map<
		string,
		ReturnType<typeof setTimeout>
	>();
	public positionChangeStateByNumber = computed<Record<string, PositionChangeState>>(() => {
		const entries = this.raceTeamsPosition();
		const stateByNumber: Record<string, PositionChangeState> = {};
		const transientStates = this.transientPositionChangeStateByNumber();

		for (const entry of entries || []) {
			if (!entry?.number) {
				continue;
			}

			stateByNumber[entry.number] = transientStates[entry.number] || 'neutral';
		}

		return stateByNumber;
	});

	public getPositionChangeState(teamNumber: string | undefined): PositionChangeState {
		if (!teamNumber) {
			return 'neutral';
		}

		return this.positionChangeStateByNumber()[teamNumber] || 'neutral';
	}

	public raceTeamsPosition$ = this.raceSession$
		.pipe(takeUntilDestroyed(this.destroyRef))
		.subscribe((data: IRaceSession) => {
			const sessionEntries = data?.entries || [];
			const classes = data?.classes || ({} as Record<string, IRaceClass>);
			sessionEntries?.forEach((entry: ISessionEntry) => {
				entry.currentDriverFullName =
					entry.currentDriver != null
						? `${entry.drivers?.[entry.currentDriver]?.firstName} ${entry.drivers?.[entry.currentDriver]?.lastName}`
						: undefined;
				entry.currentLapSectorData = this.getCurrentSectorData(
					entry.participantDetails,
					data.bestResults,
				);
				entry.lastLapSectorData = this.getLastLapSectorData(
					entry.participantDetails,
					data.bestResults,
				);
				entry.lastLapClass = this.getLastLapClass(
					entry.participantDetails,
					entry.number,
					data.bestResults,
				);
				entry.isTopSpeedOverall = this.isTopSpeedOverall(entry.number, data.bestResults);

				entry.lapsSincePit = this.getLapsSincePit(entry.number, data.status.currentLap);

				const classDetails = Object.values(classes).find((cls) => {
					const raceClass = (cls || {}) as IRaceClass;
					return raceClass.name === entry.class || raceClass.shortName === entry.class;
				});
				entry.classDetails = classDetails || ({} as IRaceClass);
			});

			const sortedEntries = sessionEntries?.length
				? sortEntriesByPosition(
						sessionEntries.filter((entry) => entry.activeStanding?.position != null),
					)
				: [];

			const previousPositions = this.currentPositionByNumber();
			const nextCurrentPositions: Record<string, number> = {};
			const activeTeamNumbers = new Set<string>();
			for (const entry of sortedEntries) {
				if (!entry?.number || !entry?.activeStanding) {
					continue;
				}

				activeTeamNumbers.add(entry.number);
				nextCurrentPositions[entry.number] = entry.activeStanding.position;

				const previousPosition = previousPositions[entry.number];
				const currentPosition = nextCurrentPositions[entry.number];

				if (
					typeof previousPosition === 'number' &&
					typeof currentPosition === 'number' &&
					currentPosition !== previousPosition
				) {
					this.setTransientPositionChange(
						entry.number,
						currentPosition < previousPosition ? 'up' : 'down',
					);
				}
			}

			this.clearTransientPositionChangesForInactiveTeams(activeTeamNumbers);

			this.previousPositionByNumber.set(previousPositions);
			this.currentPositionByNumber.set(nextCurrentPositions);
			this.raceTeamsPosition.set([...sortedEntries]);
		});

	private getLapsSincePit(entryNumber: string, currentLap: number) {
		if (!this.stints() || !currentLap) return 0;
		const stints = this.stints()[entryNumber]?.stints;
		if (!stints) return 0;
		const latest = Object.keys(stints).pop();
		const stint = stints[latest];
		return currentLap - stint.openLapNumber;
	}

	public raceStreamData$: Observable<StreamData[]> = this.raceSession$.pipe(
		map((data: IRaceSession) => {
			const entries = data?.entries || [];
			return entries?.map((e: ISessionEntry) => {
				return {
					isLive: e.playback?.isLive || false,
					carNumber: e.number || '',
					driverName: e.currentDriverFullName || '',
					hlsPlaybackUrl: e.playback?.url || '',
					teamName: e.team || '',
				} as StreamData;
			});
		}),
	);

	constructor() {
		this.isRaceHappening$.pipe(takeUntilDestroyed()).subscribe();
		this.destroyRef.onDestroy(() => {
			this.clearAllPositionChangeTimeouts();
		});
	}

	private setTransientPositionChange(teamNumber: string, state: PositionChangeState): void {
		const existingTimeout = this.positionChangeTimeoutsByNumber.get(teamNumber);
		if (existingTimeout) {
			clearTimeout(existingTimeout);
		}

		this.transientPositionChangeStateByNumber.update((currentState) => ({
			...currentState,
			[teamNumber]: state,
		}));

		const timeout = setTimeout(() => {
			this.transientPositionChangeStateByNumber.update((currentState) => {
				if (!currentState[teamNumber]) {
					return currentState;
				}

				const nextState = { ...currentState };
				delete nextState[teamNumber];
				return nextState;
			});

			this.positionChangeTimeoutsByNumber.delete(teamNumber);
		}, RaceSessionService.POSITION_CHANGE_DISPLAY_MS);

		this.positionChangeTimeoutsByNumber.set(teamNumber, timeout);
	}

	private clearTransientPositionChangesForInactiveTeams(activeTeamNumbers: Set<string>): void {
		const teamsToRemove: string[] = [];
		for (const teamNumber of this.positionChangeTimeoutsByNumber.keys()) {
			if (!activeTeamNumbers.has(teamNumber)) {
				teamsToRemove.push(teamNumber);
			}
		}

		if (teamsToRemove.length === 0) {
			return;
		}

		for (const teamNumber of teamsToRemove) {
			const timeout = this.positionChangeTimeoutsByNumber.get(teamNumber);
			if (timeout) {
				clearTimeout(timeout);
			}
			this.positionChangeTimeoutsByNumber.delete(teamNumber);
		}

		this.transientPositionChangeStateByNumber.update((currentState) => {
			const nextState = { ...currentState };
			for (const teamNumber of teamsToRemove) {
				delete nextState[teamNumber];
			}
			return nextState;
		});
	}

	private clearAllPositionChangeTimeouts(): void {
		for (const timeout of this.positionChangeTimeoutsByNumber.values()) {
			clearTimeout(timeout);
		}
		this.positionChangeTimeoutsByNumber.clear();
	}

	private getLastLapClass(
		participantDetails: any,
		teamNumber: any,
		bestResults: IBestResults,
	): string {
		const lastLapTime = participantDetails?.lastLap?.time;
		const overallBestLap = bestResults?.overall?.lap;
		if (
			overallBestLap &&
			overallBestLap.time === lastLapTime &&
			overallBestLap.participant === teamNumber
		) {
			return 'overall-best';
		}
		if (
			participantDetails?.lastLap?.time &&
			participantDetails?.bestLap?.time === participantDetails?.lastLap?.time
		) {
			return 'personal-best';
		}
		return '';
	}

	private isTopSpeedOverall(teamNumber: any, bestResults: IBestResults): boolean {
		const topSpeed = bestResults?.overall?.topSpeed;
		if (!topSpeed) return false;
		return topSpeed.participant === teamNumber;
	}

	public getCurrentSectorData(participantDetails: any, bestResults: IBestResults): ISectorData {
		if (!bestResults || !participantDetails?.lastSectors) {
			return [];
		}
		const sectorArray = Object.values(participantDetails.lastSectors)
			// filter out null
			.filter((sector: any) => sector !== null)
			.map((sector: any) => {
				let foregroundColor = '#ffffff';
				let className = '';
				if (
					sector?.time === bestResults?.overall?.sectors?.[sector.number]?.time &&
					participantDetails?.participant ===
						bestResults?.overall?.sectors?.[sector.number]?.participant &&
					participantDetails?.currentDriver ===
						bestResults?.overall?.sectors?.[sector.number]?.driver
				) {
					foregroundColor = '#ff11aa';
					className = 'overall-best';
				} else if (sector?.time === participantDetails?.bestSectors?.[sector.number]?.time) {
					foregroundColor = '#11ff11';
					className = 'personal-best';
				}
				return {
					[sector.number]: {
						time: sector.time,
						foreground: foregroundColor,
						className,
						// background: backgroundColor
					},
				} as ISectorData;
			});
		const sectorData: ISectorData = sectorArray.reduce((acc, curr) => {
			const key: any = Object.keys(curr)[0];
			acc[key] = curr[key];
			return acc;
		}, {} as ISectorData);
		return sectorData;
	}

	public getLastLapSectorData(participantDetails: any, bestResults: IBestResults): ISectorData {
		let foregroundColor = '#00000';
		// let backgroundColor = '#00000';
		if (!bestResults || !participantDetails?.lastLap?.sectors) {
			return [];
		}
		const sectorArray = Object.values(participantDetails.lastLap?.sectors).map((sector: any) => {
			if (
				sector?.time === bestResults?.overall?.sectors?.[sector.number]?.time &&
				participantDetails?.participant ===
					bestResults?.overall?.sectors?.[sector.number]?.participant &&
				participantDetails?.currentDriver === bestResults?.overall?.sectors?.[sector.number]?.driver
			) {
				foregroundColor = '#ff11aa';
			} else if (sector?.time === participantDetails?.bestSectors?.[sector.number]?.time) {
				foregroundColor = '#11ff11';
			}
			return {
				[sector.number]: {
					time: sector.time,
					foreground: foregroundColor,
				},
			} as ISectorData;
		});
		// flatten sectorArray into ISectorData
		const sectorData: ISectorData = sectorArray.reduce((acc, curr) => {
			const key: any = Object.keys(curr)[0];
			acc[key] = curr[key];
			return acc;
		}, {} as ISectorData);
		return sectorData;
	}

	public getClassDetails(carNumber: string): IRaceClass {
		const entries = this.raceTeamsPosition();
		const entry = entries.find((entry) => entry.number === carNumber);
		return entry.classDetails;
	}

	public updateSelectedClasses(selectedClasses: string[]): void {
		this.selectedClassSubject$.next(selectedClasses);
	}
}
